import type {MediaDeviceRequest} from '@pexip/media-control';
import {extractConstraints} from '@pexip/media-control';
import {isEmpty, assert} from '@pexip/utils';
import type {AudioGraph, AudioNodeInit} from '@pexip/media-processor';
import {
    createAudioGraph,
    createAudioGraphProxy,
    createChannelMergerGraphNode,
    createStreamDestinationGraphNode,
    createStreamSourceGraphNode,
    resumeAudioOnUnmute,
} from '@pexip/media-processor';

import type {
    AudioContentHint,
    MediaSignals,
    MediaTrack,
    TrackProcessor,
} from './types';
import {logger} from './logger';
import {createMediaTrack} from './utils';
import {PROCESSOR_LABELS} from './constants';

interface AudioStreamProcessorProps {
    mixWithAdditionalMedia?: boolean;
    merger?: AudioNodeInit<ChannelMergerNode, ChannelMergerNode>;
    displaySource?: AudioNodeInit<
        MediaStreamAudioSourceNode,
        MediaStreamAudioSourceNode
    >;
    audioGraph?: AudioGraph;
}

const FEATURE_KEYS = {mixWithAdditionalMedia: 'boolean'} as const;
type FeaturePropKeys = keyof typeof FEATURE_KEYS;
type FeatureProps = Pick<AudioStreamProcessorProps, FeaturePropKeys>;

export const updateFeatureProps = (
    constraints: MediaDeviceRequest['audio'],
    props: FeatureProps,
) => {
    const features: FeatureProps = {};
    if (constraints === undefined || typeof constraints === 'boolean') {
        return features;
    }

    for (const k of Object.keys(constraints)) {
        const key = k as FeaturePropKeys;
        if (!FEATURE_KEYS[key]) {
            continue;
        }
        switch (key) {
            case 'mixWithAdditionalMedia': {
                const [feature] = extractConstraints(key)(constraints);
                if (feature !== undefined && props[key] !== feature) {
                    props[key] = feature;
                    features[key] = feature;
                }
                break;
            }
        }
    }

    return features;
};

/**
 * Create an Audio Mixing Processor and will own the stream passed-in
 */
export const createAudioMixingProcess = (
    getCurrentMedia: () => MediaStream | undefined,
    signals?: MediaSignals,
    label = PROCESSOR_LABELS.AudioMixingProcessor,
): TrackProcessor => {
    const props: AudioStreamProcessorProps = {};
    return async prevMediaTrack => {
        updateFeatureProps(prevMediaTrack.getConstraints(), props);

        const displayStream = getCurrentMedia();
        const displayTrack = displayStream?.getAudioTracks?.().at(0);

        const applyConstraints: MediaTrack['applyConstraints'] =
            async constraints => {
                if (isEmpty(constraints)) {
                    return;
                }
                const features = updateFeatureProps(constraints, props);
                logger.debug(
                    {label, constraints: constraints, features},
                    'apply audio mixing constraints',
                );
                if (
                    isEmpty(features) ||
                    (props.audioGraph &&
                        ['closed', 'closing'].includes(props.audioGraph.state))
                ) {
                    return;
                }
                if (features.mixWithAdditionalMedia) {
                    const newStream = getCurrentMedia();
                    const [newTrack] =
                        getCurrentMedia()?.getAudioTracks() ?? [];
                    if (!newTrack) {
                        return;
                    }
                    if (!props.audioGraph || !props.merger) {
                        logger.debug(
                            {label},
                            'Should update the media stream directly',
                        );
                        return;
                    }
                    if (props.displaySource && props.merger) {
                        if (
                            newStream ===
                            props.displaySource.audioNode?.mediaStream
                        ) {
                            return;
                        }
                        props.audioGraph.disconnect([
                            props.displaySource,
                            props.merger,
                        ]);
                    }
                    if (newStream) {
                        props.displaySource =
                            createStreamSourceGraphNode(newStream);
                        props.audioGraph.connect([
                            props.displaySource,
                            props.merger,
                        ]);
                    }
                } else if (features.mixWithAdditionalMedia === false) {
                    // Unmixing
                    if (props.displaySource) {
                        props.displaySource.release();
                        props.audioGraph?.disconnect([props.displaySource]);
                        props.displaySource = undefined;
                    }
                }

                return Promise.resolve();
            };

        // No need to mix and use as is
        if (!prevMediaTrack.track) {
            if (!displayTrack) {
                // Do nothing
                return prevMediaTrack;
            }
            return createMediaTrack({
                label,
                kind: 'audioinput',
                previousMediaTrack: prevMediaTrack,
                input: prevMediaTrack.input,
                expectedInput: prevMediaTrack.expectedInput,
                track: displayTrack,
                constraints: prevMediaTrack.getConstraints(),
                overrideMute: true,
                mute: () => {
                    /* Do Nothing */
                },
                applyConstraints,
                getSettings: () => {
                    const mixWithAdditionalMedia =
                        !!props.mixWithAdditionalMedia;
                    const contentHint =
                        (displayTrack?.contentHint as AudioContentHint) ?? '';
                    return {
                        mixWithAdditionalMedia,
                        contentHint,
                    };
                },
            });
        }

        assert(prevMediaTrack.track, 'Main track should be available');
        const mainSource = createStreamSourceGraphNode(
            new MediaStream([prevMediaTrack.track]),
        );
        props.displaySource =
            displayStream && createStreamSourceGraphNode(displayStream);
        props.merger = createChannelMergerGraphNode();
        const destination = createStreamDestinationGraphNode({
            channelCount: 1,
            channelCountMode: 'explicit',
        });
        const initialAudioNodeConnection = [
            [mainSource, props.merger],
            [props.merger, destination],
        ];
        logger.debug(
            {initialAudioNodeConnection, label},
            'Initial AudioNodeConnection',
        );
        const audioGraph = createAudioGraphProxy(
            createAudioGraph(initialAudioNodeConnection),
            {
                connect: (target, args) => {
                    logger.debug({label, target, args}, 'connect nodes');
                },
                disconnect: (target, args) => {
                    logger.debug({label, target, args}, 'disconnect nodes');
                },
            },
        );
        props.audioGraph = audioGraph;
        const unsubscribe = resumeAudioOnUnmute(audioGraph.context)(
            prevMediaTrack.track,
        );
        if (props.displaySource) {
            props.audioGraph.connect([props.displaySource, props.merger]);
        }
        const track = destination?.node?.stream.getAudioTracks().at(0);
        assert(track, 'No audio track mixed');
        // Inherit contentHint from previous track
        track.contentHint = prevMediaTrack.track.contentHint;

        const release = async () => {
            logger.debug({label}, 'Release Media');
            unsubscribe();
            // Release Props
            await props.audioGraph?.release();
            props.audioGraph = undefined;
            props.merger = undefined;
            props.displaySource = undefined;
        };

        return Promise.resolve(
            createMediaTrack({
                label,
                kind: 'audioinput',
                previousMediaTrack: prevMediaTrack,
                input: prevMediaTrack.input,
                expectedInput: prevMediaTrack.expectedInput,
                track,
                overrideMute: true,
                mute: (toMute, _self, soft) => {
                    for (const track of mainSource.node?.mediaStream.getAudioTracks() ??
                        []) {
                        track.enabled = !toMute;
                    }
                    prevMediaTrack.mute(toMute, soft);
                },
                get muted() {
                    return mainSource.node?.mediaStream
                        .getAudioTracks()
                        .some(track => track.enabled === false);
                },
                constraints: prevMediaTrack.getConstraints(),
                applyConstraints,
                release,
                signals,
                getSettings: () => {
                    const mixWithAdditionalMedia =
                        !!props.mixWithAdditionalMedia;
                    const contentHint =
                        (track.contentHint as AudioContentHint) ?? '';
                    return {
                        mixWithAdditionalMedia,
                        contentHint,
                    };
                },
            }),
        );
    };
};
